// client/src/pages/sections/DisengagementModal.js
// Standalone modal wrapper around DisengagementFields, used by the Stale Clients
// and Client Contact NLC entry points (both previously bare window.confirm()s).
// The Client Groups MarkNlcButton embeds DisengagementFields directly instead
// (it already has its own overlay + SG/RG resolution), so it does NOT use this.
//
// Controlled-open: parent renders <DisengagementModal .../> only when open, and
// gets the normalized answers back through onConfirm(payload). The parent still
// owns the actual mark-NLC network call + optimistic update. The parent should
// close the modal only once the request has succeeded: on failure leave it open,
// pass the message as `error`, and the user's answers stay in the form.

import { useEffect, useRef, useState } from 'react';
import DisengagementFields from './DisengagementFields';
import { EMPTY_DISENGAGEMENT, validateDisengagement } from './disengagementOptions';
import './Disengagement.css';

export default function DisengagementModal({
  client,                         // { code?, name? }
  title = 'Mark as No Longer Client?',
  intro,
  confirmLabel = 'Confirm & Mark NLC',
  submitting = false,
  error = null,                   // the last failed submit's message — shown inline; answers are kept
  onConfirm,                      // (disengagementPayload) => void
  onCancel,
}) {
  const [value, setValue]   = useState(EMPTY_DISENGAGEMENT);
  const [errors, setErrors] = useState({});
  // Close on backdrop only when the press STARTED on the backdrop (mirrors the
  // Client Groups modal, so a drag that ends on the overlay doesn't dismiss).
  const overlayDownRef = useRef(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submitting, onCancel]);

  function submit() {
    const errs = validateDisengagement(value);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    // A returned promise is fine; the parent owns its failure handling.
    onConfirm?.(value);
  }

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div
      className="dis-overlay"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => { overlayDownRef.current = (e.target === e.currentTarget); }}
      onMouseUp={(e) => {
        const onBackdrop = overlayDownRef.current && e.target === e.currentTarget;
        overlayDownRef.current = false;
        if (onBackdrop && !submitting) onCancel?.();
      }}
    >
      <div className="dis-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dis-header">
          <h3>{title}</h3>
          <button type="button" className="dis-close" disabled={submitting} onClick={onCancel} aria-label="Close">×</button>
        </div>

        {client && (
          <div className="dis-summary">
            {client.code && <code>{client.code}</code>}
            <span>{client.name || '(unknown client)'}</span>
          </div>
        )}

        <div className="dis-body">
          <p className="dis-intro">
            {intro || (
              <>This client will be excluded from all future data-integrity analysis.
              Please answer the disengagement questions below so we can hand Accounts
              Receivable a complete packet — no separate form to fill out.</>
            )}
          </p>
          <DisengagementFields
            value={value}
            onChange={setValue}
            disabled={submitting}
            errors={errors}
            clientCode={client?.code}
          />
        </div>

        {hasErrors && (
          <div className="dis-form-error">Please complete the required fields highlighted above.</div>
        )}

        {error && !hasErrors && <div className="dis-form-error" role="alert">{error}</div>}

        <div className="dis-actions">
          <button type="button" className="dis-btn dis-btn--secondary" disabled={submitting} onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="dis-btn dis-btn--danger" disabled={submitting} onClick={submit}>
            {submitting ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
